import React, { useState, useEffect } from 'react';
import { Upload, Image, Video, X, CheckCircle } from 'lucide-react';
import MediaPreviewCard from '../../components/reporter/MediaPreviewCard';
import { uploadMediaApi, fetchMyMedia } from '../../utils/api/mediaApi';
import { useLanguage } from '../../context/LanguageContext';

const content = {
  ne: {
    title: 'मिडिया अपलोड',
    uploadNew: 'नयाँ मिडिया अपलोड',
    caption: 'क्याप्सन',
    captionPlaceholder: 'मिडियाको संक्षिप्त विवरण...',
    relatedEvent: 'सम्बन्धित कार्यक्रम',
    selectEvent: 'कार्यक्रम चयन गर्नुहोस्',
    chooseFile: 'फाइल चयन गर्नुहोस्',
    upload: 'अपलोड गर्नुहोस्',
    uploaded: 'मिडिया सफलतापूर्वक अपलोड गरियो!',
    recentUploads: 'हालैका अपलोडहरू',
    noMedia: 'कुनै मिडिया छैन',
    all: 'सबै',
    image: 'तस्वीर',
    video: 'भिडियो',
    failedUpload: 'अपलोड असफल भयो',
    loading: 'लोड हुँदैछ...',
  },
  en: {
    title: 'Media Uploads',
    uploadNew: 'Upload New Media',
    caption: 'Caption',
    captionPlaceholder: 'Brief description of the media...',
    relatedEvent: 'Related Event',
    selectEvent: 'Select event',
    chooseFile: 'Choose File',
    upload: 'Upload',
    uploaded: 'Media uploaded successfully!',
    recentUploads: 'Recent Uploads',
    noMedia: 'No media found',
    all: 'All',
    image: 'Image',
    video: 'Video',
    failedUpload: 'Upload failed',
    loading: 'Loading...',
  },
};

const eventOptions = ['Vision 2050 Launch Program', 'Ward Level Citizen Consultation', 'Green Itahari Plantation Drive'];

const MediaUploads = () => {
  const { language } = useLanguage();
  const t = content[language] || content.en;
  const [caption, setCaption] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('');
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [mediaType, setMediaType] = useState('image');
  const [uploaded, setUploaded] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [mediaList, setMediaList] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadMedia();
  }, []);

  const loadMedia = async () => {
    setLoadingMedia(true);
    try {
      const res = await fetchMyMedia();
      setMediaList(res.data || []);
    } catch (err) {
      setMediaList([]);
    } finally {
      setLoadingMedia(false);
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) {
      setFile(f);
      const reader = new FileReader();
      reader.onload = (ev) => setFilePreview(ev.target.result);
      reader.readAsDataURL(f);
      setMediaType(f.type.startsWith('video') ? 'video' : 'image');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      // Upload file to server via FormData
      const formData = new FormData();
      formData.append('image', file);
      const uploadRes = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001/api'}/upload/image`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('token')}`,
        },
        body: formData,
      });
      const uploadData = await uploadRes.json();
      if (!uploadData.success) throw new Error('File upload failed');

      // Save media record
      await uploadMediaApi({
        caption,
        relatedEvent: selectedEvent,
        fileUrl: uploadData.url,
        fileType: mediaType,
      });

      setUploaded(true);
      loadMedia();
      setTimeout(() => {
        setUploaded(false);
        setCaption('');
        setSelectedEvent('');
        setFile(null);
        setFilePreview(null);
      }, 3000);
    } catch (err) {
      setError(err.message || t.failedUpload);
    } finally {
      setUploading(false);
    }
  };

  const filteredMedia = filterType === 'all'
    ? mediaList
    : mediaList.filter((m) => m.fileType === filterType);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-extrabold text-char-900">{t.title}</h2>

      {/* Error message */}
      {error && (
        <div className="rounded-2xl bg-red-50 px-4 py-3 text-sm font-bold text-red-600 border border-red-200">
          {error}
        </div>
      )}

      {/* Upload form */}
      <div className="rounded-3xl border border-char-100 bg-white p-5 shadow-card sm:p-6">
        <h3 className="mb-4 text-base font-extrabold text-char-900">{t.uploadNew}</h3>

        {uploaded ? (
          <div className="flex items-center gap-3 rounded-2xl bg-primary-100 px-4 py-3">
            <CheckCircle size={18} className="text-primary" />
            <p className="text-sm font-bold text-primary">{t.uploaded}</p>
          </div>
        ) : (
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-xs font-extrabold uppercase tracking-wider text-char-500">{t.caption}</label>
                <input
                  type="text"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  required
                  placeholder={t.captionPlaceholder}
                  className="w-full rounded-xl border border-char-100 bg-char-50 px-4 py-2.5 text-sm text-char-900 outline-none transition-all focus:border-primary-300 focus:bg-white focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-extrabold uppercase tracking-wider text-char-500">{t.relatedEvent}</label>
                <select
                  value={selectedEvent}
                  onChange={(e) => setSelectedEvent(e.target.value)}
                  className="w-full rounded-xl border border-char-100 bg-char-50 px-4 py-2.5 text-sm text-char-900 outline-none transition-all focus:border-primary-300 focus:bg-white focus:ring-2 focus:ring-primary-100"
                >
                  <option value="">{t.selectEvent}</option>
                  {eventOptions.map((ev) => (
                    <option key={ev} value={ev}>{ev}</option>
                  ))}
                </select>
              </div>
            </div>

            {filePreview ? (
              <div className="relative inline-block">
                {mediaType === 'video' ? (
                  <div className="flex items-center gap-2 rounded-2xl bg-char-50 px-4 py-3">
                    <Video size={20} className="text-char-400" />
                    <span className="text-sm text-char-600">{file?.name}</span>
                  </div>
                ) : (
                  <div className="relative inline-block">
                    <img src={filePreview} alt="Preview" className="h-32 w-48 rounded-2xl object-cover" />
                    <button
                      type="button"
                      onClick={() => { setFile(null); setFilePreview(null); }}
                      className="absolute -right-2 -top-2 rounded-full bg-white p-1 shadow-sm hover:bg-error-50 hover:text-error transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <label className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-char-200 bg-char-50 px-4 py-6 transition-all hover:border-primary-300 hover:bg-primary-50">
                <Upload size={24} className="text-char-300" />
                <span className="mt-2 text-xs font-semibold text-char-500">{t.chooseFile}</span>
                <input type="file" accept="image/*,video/*" onChange={handleFileChange} className="hidden" required />
              </label>
            )}

            <button
              type="submit"
              disabled={uploading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 text-sm font-extrabold text-white shadow-green transition-all hover:-translate-y-0.5 hover:bg-primary-500 hover:shadow-green-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload size={16} />
              {uploading ? (language === 'ne' ? 'अपलोड हुँदैछ...' : 'Uploading...') : t.upload}
            </button>
          </form>
        )}
      </div>

      {/* Recent uploads */}
      <div>
        <h3 className="mb-4 text-lg font-extrabold text-char-900">{t.recentUploads}</h3>
        <div className="mb-4 flex flex-wrap gap-2">
          {['all', 'image', 'video'].map((type) => (
            <button
              key={type}
              onClick={() => setFilterType(type)}
              className={`rounded-xl px-4 py-2 text-xs font-extrabold uppercase tracking-wider transition-all ${
                filterType === type
                  ? 'bg-primary text-white shadow-sm'
                  : 'bg-white text-char-500 border border-char-100 hover:border-primary-200 hover:text-primary'
              }`}
            >
              {t[type] || type}
            </button>
          ))}
        </div>

        {loadingMedia ? (
          <div className="flex items-center justify-center rounded-3xl border border-char-100 bg-white py-12">
            <p className="text-sm text-char-400">{t.loading}</p>
          </div>
        ) : filteredMedia.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-3xl border border-char-100 bg-white py-12">
            <Image size={36} className="text-char-300" />
            <p className="mt-3 text-sm text-char-500">{t.noMedia}</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredMedia.map((item) => (
              <MediaPreviewCard key={item._id} media={item} language={language} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaUploads;
